import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * Field type — matches DB CHECK on `internal.custom_field_definitions.field_type`
 * (migration 0009). Architecture admin-arch §2.2 defines four types: number,
 * text, date, select. The previous `single_select`/`multi_select`/`boolean`
 * variants were rejected — multi-select is encoded as `select` with multi flag
 * in `options`, boolean uses a 2-option select.
 */
export const customFieldTypeEnum = z.enum([
    'number',
    'text',
    'date',
    'select',
]);
/**
 * Entity type the field targets. MVP only `'track'` (admin-arch §2.2 CHECK).
 */
export const customFieldEntityEnum = z.enum(['track']);
/**
 * `select` options payload shape — backend additionally validates this server
 * side and rejects mismatches with `VALIDATION_ERROR`.
 */
export const customFieldSelectOptionsSchema = z.object({
    values: z.array(z.string().min(1).max(255)).min(1),
    multi: z.boolean().optional(),
});
/**
 * Numeric range options — used for `field_type='number'` to constrain inputs.
 */
export const customFieldNumberOptionsSchema = z.object({
    min: z.number().optional(),
    max: z.number().optional(),
});
export const customFieldOptionsSchema = z.union([
    customFieldSelectOptionsSchema,
    customFieldNumberOptionsSchema,
    z.record(z.string(), z.unknown()),
]);
export const customFieldDefinitionSchema = z.object({
    id: uuidSchema,
    entity_type: customFieldEntityEnum,
    name: z.string().min(1).max(255),
    code: z.string().min(1).max(64),
    field_type: customFieldTypeEnum,
    options: customFieldOptionsSchema.nullable(),
    is_required: z.boolean(),
    is_active: z.boolean(),
    pending_deletion_action_id: uuidSchema.nullable(),
    display_order: z.number().int().nullable(),
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
});
export const customFieldCreateSchema = z
    .object({
    entity_type: customFieldEntityEnum.default('track'),
    name: z.string().min(1).max(255),
    code: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[a-z][a-z0-9_]*$/, 'Lowercase snake_case'),
    field_type: customFieldTypeEnum,
    options: customFieldOptionsSchema.optional(),
    is_required: z.boolean().default(false),
    display_order: z.number().int().optional(),
})
    .superRefine((value, ctx) => {
    if (value.field_type === 'select') {
        const parsed = customFieldSelectOptionsSchema.safeParse(value.options);
        if (!parsed.success) {
            ctx.addIssue({
                code: 'custom',
                path: ['options'],
                message: 'Select fields require options.values: string[]',
            });
        }
    }
});
export const customFieldUpdateSchema = z
    .object({
    name: z.string().min(1).max(255).optional(),
    options: customFieldOptionsSchema.optional(),
    is_required: z.boolean().optional(),
    display_order: z.number().int().optional(),
})
    .refine((obj) => Object.keys(obj).length > 0, 'At least one field must be provided');
/**
 * Two-admin destructive-action workflow (admin-arch §3.11.1).
 * `request_deletion` opens a pending_action; another super-admin must
 * `approve` or the initiator can `revoke` until decision.
 */
export const customFieldDeletionRequestSchema = z.object({
    reason: z.string().min(1).max(2000).optional(),
});
//# sourceMappingURL=custom-field.js.map